
import { Invoice } from "@/domain/entities/PrintReceipt";
import { Options, Vue, prop } from "vue-class-component";
import { AccountController } from '@/app/ui/controllers/AccountController';

interface Receiver {
  via: string;
  viaExpanded: boolean;
  value: string;
  format: string;
  formatExpanded: boolean;
  error: boolean;
  errorMessage: string;
  errorFormat: boolean;
  errorFormatMessage: string;
}

class Props {
  viaOptions = prop<any[]>({
    type: Array,
    default: () => []
  });
  formatOptions = prop<any[]>({
    type: Array,
    default: () => []
  });
  maxHeight = prop<string>({
    type: String,
    default: "524px"
  });
  maxItemOverflow = prop<number>({
    type: Number,
    default: 9
  });
  optional = prop<boolean>({
    default: false,
    type: Boolean
  });
  invoiceDetail = prop<Invoice>({
    default: () => new Invoice()
  })
}

export default class SendInvoiceReceiverRows extends Vue.with(Props) {
  // Local receivers data
  localReceivers: Receiver[] = [];

  mounted() {
    this.initReceivers();
  }

  // Computed
  get receivers() {
    return this.localReceivers;
  }

  // Methods
  initReceivers() {
    this.localReceivers = [this.createEmptyReceiver()];
    this.emitUpdate();
  }

  createEmptyReceiver(): Receiver {
    return {
      via: "email",
      viaExpanded: false,
      value: "",
      format: "",
      formatExpanded: false,
      error: false,
      errorMessage: "",
      errorFormat: false,
      errorFormatMessage: ""
    };
  }

  getPlaceholder(via: string) {
    return via === "email" ? this.$t("tagihan.create.inputEmail") : this.$t("tagihan.create.inputWhatsappNumber");
  }

  addReceiver() {
    this.localReceivers.push(this.createEmptyReceiver());
    this.emitUpdate();
  }

  removeReceiver(index: number) {
    if (this.localReceivers.length > 1) {
      this.localReceivers.splice(index, 1);
    } else {
      this.localReceivers[index] = this.createEmptyReceiver();
    }
    this.emitUpdate();
  }

  onSelectVia(index: number, option: any) {
    this.localReceivers[index].via = option.value;
    this.localReceivers[index].viaExpanded = false;
    this.localReceivers[index].value = "";
    this.localReceivers[index].error = false;
    this.localReceivers[index].errorMessage = "";
    this.emitUpdate();
  }

  onSelectFormat(index: number, option: any) {
    this.localReceivers[index].format = option.value;
    this.localReceivers[index].formatExpanded = false;
    this.localReceivers[index].errorFormat = false;
    this.localReceivers[index].errorFormatMessage = "";
    this.emitUpdate();
  }

  validateReceiver(index: number, value: string) {
    const receiver = this.localReceivers[index];

    if (!value && this.optional) {
      receiver.error = false;
      receiver.errorMessage = "";
      this.emitUpdate();
      return;
    }

    if (!value) {
      receiver.error = true;
      receiver.errorMessage =
        receiver.via === "email"
          ? this.$t("tagihan.create.mandatoryEmail")
          : this.$t("tagihan.create.mandatoryWhatsapp");
      this.emitUpdate();
      return;
    }

    if (receiver.via === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      receiver.error = !emailRegex.test(value);
      receiver.errorMessage = receiver.error ? this.$t("tagihan.create.formatEmail") : "";
    } else {
      const phoneRegex = /^(\+?62|0)[0-9]{9,12}$/;
      receiver.error = !phoneRegex.test(value);
      receiver.errorMessage = receiver.error
        ? this.$t("tagihan.create.formatWhatsapp")
        : "";
    }

    const findIndexReceiver = this.localReceivers.findIndex(
      (item, indexItem) => indexItem !== index && item.value === value
    );

    if (findIndexReceiver !== -1) {
      receiver.error = true;
      receiver.errorMessage =
        receiver.via === "email"
          ? this.$t("tagihan.create.alreadyAddedEmail")
          : this.$t("tagihan.create.alreadyAddedWhatsapp");
    }

    this.emitUpdate();
  }

  validateFormat(index: number, openSelect: string) {
    if (!openSelect) {
      return;
    }

    const receiver = this.localReceivers[index];

    if (!receiver.format) {
      receiver.errorFormat = true;
      receiver.errorFormatMessage = this.$t("tagihan.create.mandatoryFormat");
    } else {
      receiver.errorFormat = false;
      receiver.errorFormatMessage = "";
    }

    this.emitUpdate();
  }

  // Get valid receivers (for parent usage)
  getValidReceivers(): Receiver[] {
    return this.localReceivers.filter(
      (r) => r.value && r.format && !r.error && !r.errorFormat
    );
  }

  // Check if all receivers are valid
  get isValid(): boolean {
    return this.localReceivers.every((r) => {
      const hasValue = !!r.value;

      if (this.optional && !hasValue) {
        return true; // optional & empty → valid
      }

      return hasValue && r.format && !r.error && !r.errorFormat;
    });
  }

  // Emit update to parent
  emitUpdate() {
    this.$emit("update", {
      receivers: this.localReceivers,
      validReceivers: this.getValidReceivers(),
      isValid: this.isValid
    });
  }

  // Public method to get receivers data
  getReceivers() {
    return this.localReceivers;
  }

  get accountCurrency() {
    return (
      (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ?? "IDR"
    );
  }
}
